import React, { Fragment, memo, ReactNode, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { BasicCheckbox } from 'src/components/shared/form/BasicCheckbox'

interface Props {
  isOpen: boolean
  title: string
  description: string
  /** Extra content under the description — e.g. the file names a delete will remove. */
  details?: ReactNode
  /**
   * When set, a checkbox with this label is shown under the details and Confirm stays disabled
   * until it is ticked. Used for platform-wide and money-moving actions. Resets on every open.
   */
  acknowledgement?: ReactNode
  confirmLabel: string
  /** `red` confirm for destructive actions, `dark` otherwise — never `primary` in a modal footer. */
  isDestructive?: boolean
  isSubmitting: boolean
  onConfirm(): void
  onClose(): void
  confirmDataTestId?: string
}

const ConfirmActionModalComponent = ({
  isOpen,
  title,
  description,
  details,
  acknowledgement,
  confirmLabel,
  isDestructive = false,
  isSubmitting,
  onConfirm,
  onClose,
  confirmDataTestId,
}: Props) => {
  const [isAcknowledged, setIsAcknowledged] = useState<boolean>(false)

  // A fresh open must never inherit the previous confirmation's tick. Cleared on CLOSE, so the
  // reopened modal's first render is already unticked (resetting on open left one stale frame
  // with Confirm enabled).
  useEffect(() => {
    if (!isOpen) {
      setIsAcknowledged(false)
    }
  }, [isOpen])

  const handleAcknowledgementChange = useCallback((event: React.ChangeEvent<any>) => {
    setIsAcknowledged(Boolean(event.target.checked))
  }, [])

  const isBlockedByAcknowledgement = Boolean(acknowledgement) && !isAcknowledged

  return (
    <CommonModal
      isOpen={isOpen}
      title={title}
      className={tw`max-w-[480px]`}
      onClose={onClose}
      buttons={
        <Fragment>
          <Button size="small" color="grey" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="small"
            color={isDestructive ? 'red' : 'dark'}
            loading={isSubmitting}
            disabled={isSubmitting || isBlockedByAcknowledgement}
            onClick={onConfirm}
            data-testid={confirmDataTestId}
          >
            {confirmLabel}
          </Button>
        </Fragment>
      }
    >
      <div css={tw`flex flex-col gap-3`}>
        <Typography variant="body2">{description}</Typography>
        {details}
        {acknowledgement ? (
          <BasicCheckbox
            name="confirmActionAcknowledgement"
            label={acknowledgement}
            value={isAcknowledged}
            onChange={handleAcknowledgementChange}
            inputProps={{ 'data-testid': 'confirm-action-acknowledgement' }}
          />
        ) : null}
      </div>
    </CommonModal>
  )
}

export const ConfirmActionModal = memo(ConfirmActionModalComponent)
